/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import { DisclaimerBanner, LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ReportsV4ColumnChooser, convertColumnsToOldUIReportAggregation, filterCategories, getRejectedColumns } from '@bingads-webui-campaign/reports-v4-column-chooser';
import { ViewItem, useInsertionOrderData } from '@bingads-webui-campaign/reports-v4-component';
import { ReportsV4Footer } from '@bingads-webui-campaign/reports-v4-footer';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { defaultSchedule } from '@bingads-webui-campaign/reports-v4-schedule-frequency-icon-editor';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { timeZones } from '@bingads-webui/timezone-details';
import { MessageBar, Separator, Stack, DetailsRow, IconButton, GroupHeader } from '@fluentui/react';

import { buildColumns } from '../helpers/grid-helper';
import { getColumnChooserSelectedKey, getColumnsByView, getInitialInsertionOrderSelectorItems, getRejectedColumnsByView, getSortedColumnByView, isInsertionOrderMonthlyView, isInsertionOrderReport } from '../helpers/insertion-order-helper';
import { bindOnRenderRowsForInsertionOrderReport } from '../helpers/insertion-order-column-helper';
import { useWarnings } from '../helpers/use-warnings';
import { iconClassNames } from '../style';
import { collapsedRowApiFormat } from './converters/convert-collapsed-rows';
import { DatePicker } from './date-picker';
import { tryGetUpdatedTimeRange } from './date-picker/date-picker-utils';
import { NameEditor } from './name-editor';
import { NavigateDialog } from './navigate-dialog';
import { ReportGrid } from './report-grid';
import { ReportGridModal } from './report-grid-modal/index';
import { ReportsV4SummaryBar } from './summary-bar';
import { TimeZoneSelector } from './timezone-selector/timezone-selector';
import { Warning } from './warning';
import { SidePanelReportGrid } from './side-panel-report-grid-modal';
import { getSearchTermDetails, showTermItem, getGroupProps } from '../helpers/search-category-helper';

export function getDetailListColumns(reportParam, reportSchema, i18n, permissions, isAccountScope) {
  if (!reportParam || !reportSchema) {
    return [];
  }
  const customizeRenderParams = {
    reportParam,
    permissions,
    bindOnRenderRows: [bindOnRenderRowsForInsertionOrderReport],
    isAccountScope,
  };
  const newColumns = buildColumns(reportParam.columns, reportSchema.Columns, reportParam.conditionalFormats, i18n, customizeRenderParams);
  return newColumns;
}

export function PreviewPageImplForCommon(props) {
  const {
    classes,
    reportAction,
    reportId,
    reportSchema,
    reportParam,
    setReportParam,
    savedReportParam,
    reportDataItems,
    reportModalParam,
    setReportModalParam,
    reportModalDataItems,
    modalTotalCount,
    totalCount,
    onSave,
    onSaveAs,
    onDownload,
    timeTravelHistory,
    perfMarker,
    actionPerfMarker,
    ctrLogger,
    placeHolderError,
    forceRefreshData,
    categoryGroup,
    setCategoryGroup,
    parentIndex,
  } = props;

  if (!reportSchema) {
    throw new Error('Missing reportSchema!');
  }

  if (!reportParam) {
    throw new Error('Missing reportParam!');
  }

  const { scope } = useContext(ScopeContext);
  const i18n = useContext(I18nContext);
  const permissions = useContext(PermissionsContext);

  const [warnings, patchWarnings] = useWarnings();
  const history = useReportsHistory();
  const { query } = history;
  const { insertionOrderItems } = useInsertionOrderData({
    i18n,
    extraPredicate: reportParam.insertionOrderSelectorValue ? `{"Field":"InsertionOrderId","Operator":3,"Values":"${reportParam.insertionOrderSelectorValue.join(',')}"}` : undefined,
  });

  const [disableNavigateDialogOnce, setDisableNavigateDialogOnce] = useState(false);
  const [searchHistorySelectorOptions, setSearchHistorySelectorOptions] = useState([]);
  const [dimensionBannerVisible, setDimensionBannerVisible] = useState(query.isDrprecated === 'true');
  const [selectedInsertionOrderViewItem, setSelectedInsertionOrderViewItem] = useState(reportParam.insertionOrderViewType || ViewItem.Monthly);
  const [insertionOrderSelected, setInsertionOrderSelected] = useState([]);
  const [, setDetailStr] = useState('');

  const isInsertionOrderReportEnabled = isInsertionOrderReport({ reportType: reportSchema.ReportType, permissions });

  if (query.isDrprecated || query.ioid || query.entrypoint) {
    if (isInsertionOrderReportEnabled) {
      ctrLogger.click({ reportType: reportSchema.ReportType, target: 'entrypoint', extraData: { entrypoint: query.entrypoint, ioid: query.ioid } });
    }
    delete query.isDrprecated;
    delete query.ioid;
    delete query.entrypoint;
    const search = new URLSearchParams(query);
    window.history.replaceState(null, null, `${window.location.pathname}?${search}`);
  }

  const isGenreReport = permissions.dynamic.GenreTargets && reportSchema.ReportType === 'GenrePerformanceReport';

  useEffect(() => {
    if (isInsertionOrderReportEnabled
      && !_.isEmpty(insertionOrderItems)
      && _.isEmpty(insertionOrderSelected)) {
      const initialInsertionOrderSelected = getInitialInsertionOrderSelectorItems({
        allInsertionOrders: insertionOrderItems,
        insertionOrderIds: reportParam.insertionOrderSelectorValue,
      });
      if (!_.isEmpty(initialInsertionOrderSelected)) {
        setInsertionOrderSelected(insertionOrderItems);
      }
    }
  }, [insertionOrderItems, reportParam.insertionOrderSelectorValue, reportSchema.ReportType, permissions, reportAction, insertionOrderSelected, isInsertionOrderReportEnabled]);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      // location is an object like window.location
      if (!location.pathname.includes('reports-v4') && !reportDataItems) {
        ctrLogger.click({ target: 'LeavePageBeforeLoaded', extraData: { reportParam } });
        unlisten();
      }
    });
    window.addEventListener('beforeunload', () => {
      if (!reportDataItems) {
        ctrLogger.click({ target: 'LeavePageBeforeLoaded', extraData: { reportParam } });
      }
    });
    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDataItems, reportParam]);

  // #region column-chooser
  const [isColumnChooserOpened, setIsColumnChooserOpened] = useState(false);
  function toggleIsColumnChooserOpened() {
    setIsColumnChooserOpened(isOpened => !isOpened);
  }

  function onColumnChooserSaveClick(columns) {
    toggleIsColumnChooserOpened();
    // Not only update columns, but all things calcuated based on columns
    // Update TimeGrain:
    const newTimeRange = tryGetUpdatedTimeRange({
      newTimeGrain: convertColumnsToOldUIReportAggregation(columns),
      prevTimeGrain: convertColumnsToOldUIReportAggregation(reportParam.columns),
      prevTimeRange: reportParam.dateRange,
      reportSchema,
    });

    const isAccountScope = scope.location.search.indexOf('aid') >= 0;
    const restrictedColumnSet = getRejectedColumnsByView({
      reportType: reportSchema.ReportType,
      restrictedColumnSets: reportSchema.Columns.displayRestricted,
      permissions,
      viewType: undefined, // No need to add Date column of another view type
      isCustomerScope: !isAccountScope,
    });
    const newColumns = [...columns];
    restrictedColumnSet.forEach((c) => {
      if (!_.include(columns, c)) {
        newColumns.push(c);
      }
    });

    const newConditionalFormats = _.pick(reportParam.conditionalFormats, newColumns);
    const addedColumns = _.difference(newColumns, reportParam.columns);
    const removedColumns = _.difference(reportParam.columns, newColumns);
    const totalColumnChanges = addedColumns.length + removedColumns.length;

    setReportParam({
      ...reportParam, columns: newColumns, conditionalFormats: newConditionalFormats, dateRange: newTimeRange,
    });
    ctrLogger.click({
      target: 'ColumnChooser',
      extraData: {
        columns: newColumns, removedColumns, addedColumns, totalColumnChanges,
      },
    });
  }

  function onColumnChooserCancelClick() {
    toggleIsColumnChooserOpened();
  }

  function renderColumnChooer() {
    let { categories } = reportSchema.Columns;
    const isAccountScope = scope.location.search.indexOf('aid') >= 0;

    const rejectedColumns = getRejectedColumns(reportSchema.Columns.piloted, permissions, !isAccountScope, true);
    const customizeRejectedColumns = getRejectedColumnsByView({
      reportType: reportSchema.ReportType,
      restrictedColumnSets: reportSchema.Columns.displayRestricted,
      permissions,
      viewType: selectedInsertionOrderViewItem,
      isCustomerScope: !isAccountScope,
    });
    customizeRejectedColumns.forEach(column => rejectedColumns.add(column));
    categories = filterCategories(categories, { rejectedColumns });

    // This is a classic example of over engineering, premature optimization and lack of static type.
    const aggregationExclusion = {};
    categories.forEach((categorty) => {
      categorty.options.forEach(option =>
        option.forbiddenTimeGrains && option.forbiddenTimeGrains.forEach((timeAggregation) => {
          if (!aggregationExclusion[timeAggregation]) {
            aggregationExclusion[timeAggregation] = [];
          }
          aggregationExclusion[timeAggregation].push(option.key);
        }));
    });

    const selectedKeys = getColumnChooserSelectedKey({
      reportType: reportSchema.ReportType,
      permissions,
      columns: reportParam.columns,
      rejectedColumns,
    });

    return (
      <ReportsV4ColumnChooser
        AggregationExclusion={aggregationExclusion}
        isTimeColumnMandatory={!reportSchema.ReportAggregation.enum.includes('Summary')}
        MandateColumnSet={reportSchema.MandateColumnSet}
        MissingMandatoryColumnError={reportSchema.MissingMandatoryColumnError}
        categories={categories}
        exclusionSets={reportSchema.ExclusionSets || {}}
        selectedKeys={selectedKeys}
        onCancelClick={onColumnChooserCancelClick}
        onSaveClick={onColumnChooserSaveClick}
      />
    );
  }
  // #endregion column-chooser

  // #region report-modal
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isSearchInsightReportOpen, setisSearchInsightReportOpen] = useState(false);
  const isSearchInsightReportGridModal = permissions && permissions.dynamic && permissions.dynamic.SearchInsightPerformanceReport && (reportSchema.ReportType === 'SearchInsightPerformanceReport');

  // Add an additional condition once ui can know if there is collapsed data from MT
  const hasReportGridModal = permissions && permissions.dynamic && permissions.dynamic.CollapsedRow && (reportSchema.ReportType === 'PublisherUsagePerformanceReport');

  function toggleIsReportModalOpen() {
    setIsReportModalOpen(isOpened => !isOpened);
  }

  useEffect(() => {
    function triggerReportModalDataFetch() {
      setReportModalParam({
        ...reportParam,
        GridPagination: {
          RequestedPage: 1,
          RowsPerPage: 20,
        },
        CollapsedRow: collapsedRowApiFormat(isReportModalOpen),
      });
    }

    function addOtherURLsRow() {
      const hasOtherUrlsRow = reportDataItems && reportDataItems.length > 0 && reportDataItems[reportDataItems.length - 1].key === 'Other URLs';
      if (hasReportGridModal && reportDataItems && !hasOtherUrlsRow) {
        reportDataItems.push({
          RowId: 0,
          key: 'Other URLs',
          toggleIsReportModalOpen,
        });
      }
    }

    if (hasReportGridModal) {
      addOtherURLsRow();
      if (isReportModalOpen) {
        triggerReportModalDataFetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReportModalOpen, reportDataItems, reportParam]);
  // #endregion report-modal

  function toggleIsSearchReportModalOpen() {
    setisSearchInsightReportOpen(isOpened => !isOpened);
  }

  function onToggleCollapse(group) {
    showTermItem(group, setReportModalParam, reportParam);

    if (categoryGroup && categoryGroup.length > 0) {
      categoryGroup.forEach((item) => {
        if (item.key !== group.key) {
          // eslint-disable-next-line no-param-reassign
          item.isCollapsed = true;
        }
      });
    }
  }

  useEffect(() => {
    if (isSearchInsightReportGridModal && modalTotalCount === 0 && reportModalDataItems &&
      reportModalDataItems.length === 0 && !_.isEmpty(reportDataItems) && parentIndex >= 0) {
      reportDataItems[parentIndex].SearchTermDetailsStr = 'No data';
      setDetailStr('No data');
    }
  }, ([isSearchInsightReportGridModal, parentIndex, reportModalDataItems]));

  useEffect(() => {
    if (isSearchInsightReportGridModal && !_.isEmpty(reportDataItems) && !_.isEmpty(reportModalDataItems)
    ) {
      const searchCategory = _.first(reportModalDataItems).SearchCategory.Value;
      const details = getSearchTermDetails(reportModalDataItems, i18n, modalTotalCount);
      reportDataItems.forEach((item) => {
        if (item.RowType &&
          item.RowType === 'Normal' &&
          item.SearchCategory &&
          item.SearchCategory.Value === searchCategory) {
          // eslint-disable-next-line no-param-reassign
          item.SearchTermDetailsStr = details;
        }
      });
      setDetailStr(details);
    }
  }, ([isSearchInsightReportGridModal, reportModalDataItems]));


  useEffect(() => {
    function buildSearchTermRow() {
      const groups = [];
      reportDataItems.forEach((item, index) => {
        if (item.RowType && item.RowType === 'Normal') {
          groups.push({
            ...item, startIndex: index, count: 1, level: 0, isCollapsed: true, isParent: true, toggleIsSearchReportModalOpen,
          });
        }
      });
      setCategoryGroup(groups);
    }
    if (isSearchInsightReportGridModal && reportDataItems && reportDataItems.length > 0) {
      buildSearchTermRow();
    }
  }, ([isSearchInsightReportGridModal, reportDataItems, reportParam]));

  const isAccountScope = scope.location.search.indexOf('aid') >= 0;

  const isDirty = (() => {
    // if user does not do any changes, `savedReportParam` should be `undefined` and no need to reset.
    if (!savedReportParam) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { GridPagination, ...filteredSavedReportParam } = savedReportParam;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { GridPagination: savedGridPagination, ...filteredReportParam } = reportParam;
    return JSON.stringify(filteredSavedReportParam) !== JSON.stringify(filteredReportParam);
  })();

  function onUndoClick() {
    timeTravelHistory.back();
    ctrLogger.click({ target: 'Undo' });
  }

  function onRedoClick() {
    timeTravelHistory.forward();
    ctrLogger.click({ target: 'Redo' });
  }

  function onResetClick() {
    setReportParam(savedReportParam);
    ctrLogger.click({ target: 'Reset' });
  }

  async function onSchedulerSaveClick(schedule) {
    ctrLogger.click({ target: 'Scheduling', extraData: { reportType: reportParam.reportType, ...schedule } });
    const newReportParam = { ...reportParam, Format: schedule.Format, schedule };
    if (reportAction === 'edit') {
      setReportParam(newReportParam);
      await onSave(newReportParam);
    } else {
      setDisableNavigateDialogOnce(true);
      await onSaveAs(newReportParam);
    }
  }

  function onNameChange(reportName) {
    setReportParam({ ...reportParam, reportName });
    ctrLogger.click({ target: 'InlineEditName', extraData: { reportType: reportParam.reportType, reportName } });
  }

  function onClickSave(...args) {
    onSave(...args);
    ctrLogger.click({ target: 'Save', extraData: { reportType: reportParam.reportType } });
  }

  function onClickSaveAs(reportName) {
    setDisableNavigateDialogOnce(true);
    onSaveAs({ ...reportParam, reportName });
    ctrLogger.click({ target: 'SaveAs', extraData: { reportType: reportParam.reportType, reportName } });
    if (isInsertionOrderReportEnabled) {
      ctrLogger.click({ target: 'InsertionOrderViewType-SaveAs', extraData: { reportType: reportParam.reportType, viewType: reportParam.insertionOrderViewType } });
    }
  }

  function onTimeZoneChange({ timeZone }) {
    setReportParam({ ...reportParam, timeZone });
    ctrLogger.click({ target: 'TimeZoneSelector', extraData: { reportType: reportParam.reportType, timeZone } });
  }

  function onDatePickerChange(dateRange) {
    setReportParam({ ...reportParam, dateRange });
    ctrLogger.click({ target: 'DatePicker', extraData: { reportType: reportParam.reportType, dateRange } });
  }

  function onAccountSelectorChange(scopeSelector) {
    setReportParam({ ...reportParam, scopeSelector });
    ctrLogger.click({ target: 'ScopeSelector', extraData: { reportType: reportParam.reportType, scopeSelector } });
  }

  function onFilterChange({ filters }) {
    setReportParam({ ...reportParam, filters });
    ctrLogger.click({ target: 'Filter', extraData: { reportType: reportParam.reportType, filters } });
  }

  function onConditionalFormatsChange(conditionalFormats) {
    setReportParam({ ...reportParam, conditionalFormats });
    ctrLogger.click({ target: 'ConditionalFormatting', extraData: { reportType: reportParam.reportType, conditionalFormats } });
  }

  function onPageChange(pageNumber) {
    setReportParam({
      ...reportParam,
      GridPagination: {
        RequestedPage: pageNumber,
        RowsPerPage: reportParam.GridPagination.RowsPerPage,
      },
    });
    ctrLogger.click({ target: 'PageNumber', extraData: { reportType: reportParam.reportType, pageNumber } });
  }

  function onPageSizeChange(pageSize) {
    setReportParam({
      ...reportParam,
      GridPagination: {
        RequestedPage: 1,
        RowsPerPage: pageSize,
      },
    });
    ctrLogger.click({ target: 'PageSize', extraData: { reportType: reportParam.reportType, pageSize } });
  }

  function onSort(column) {
    setReportParam({
      ...reportParam,
      sortedColumn: column,
    });
    ctrLogger.click({ target: 'Sort', extraData: { reportType: reportParam.reportType, sortedColumn: column } });
  }

  function onModalPageChange(pageNumber) {
    setReportModalParam({
      ...reportModalParam,
      GridPagination: {
        RequestedPage: pageNumber,
        RowsPerPage: reportModalParam.GridPagination.RowsPerPage,
      },
    });
    ctrLogger.click({ target: 'ModalPageNumber', extraData: { pageNumber } });
  }

  function onModalPageSizeChange(pageSize) {
    setReportModalParam({
      ...reportModalParam,
      GridPagination: {
        RequestedPage: 1,
        RowsPerPage: pageSize,
      },
    });
    ctrLogger.click({ target: 'ModalPageSize', extraData: { pageSize } });
  }

  function onModalSort(column) {
    // reset the page number to 1 when sorting
    setReportModalParam({
      ...reportModalParam,
      GridPagination: { RequestedPage: 1, RowsPerPage: reportModalParam.GridPagination.RowsPerPage },
      sortedColumn: column,
    });
    ctrLogger.click({ target: 'ModalSort', extraData: { sortedColumn: column } });
  }

  function onClickDownload(format) {
    onDownload(format, isDirty);
    ctrLogger.click({ target: 'Download', extraData: { reportType: reportParam.reportType, format } });
  }

  function onColumnChooserOpen() {
    toggleIsColumnChooserOpened();
    ctrLogger.click({ target: 'ColumnChooserOpened' });
  }

  const onInsertionOrderViewItemChange = (viewItem) => {
    const oldViewItem = selectedInsertionOrderViewItem;
    setSelectedInsertionOrderViewItem(viewItem);
    const newColumns = getColumnsByView({ defaultColumns: reportParam.columns, viewType: viewItem });
    const newGridPagination = {
      RequestedPage: 1,
      RowsPerPage: viewItem === ViewItem.Monthly ? 200 : 20,
    };
    const newSortedColumn = {
      key: getSortedColumnByView({ viewType: viewItem }),
      isSortedDescending: true,
    };
    setReportParam({
      ...reportParam,
      insertionOrderViewType: viewItem,
      columns: newColumns,
      GridPagination: newGridPagination,
      sortedColumn: newSortedColumn,
      skipResetPagination: true, // When insertion order view item changes, sortedColumn changes but we don't want to reset pagination.
    });
    ctrLogger.click({ target: 'InsertionOrderViewItemChange', extraData: { reportType: reportParam.reportType, viewItem, oldViewItem } });
  };

  const onInsertionOrderSelectorChange = (selectedInsertionOrders) => {
    setInsertionOrderSelected(selectedInsertionOrders);
    const insertionOrderIds = _.map(selectedInsertionOrders, item => item.id);
    setReportParam({
      ...reportParam,
      insertionOrderSelectorValue: insertionOrderIds,
    });
    ctrLogger.click({ target: 'InsertionOrderSelector', extraData: { reportType: reportParam.reportType, insertionOrderIds } });
  };

  function renderHeader() {
    return (
      <Stack className={`header ${classes.reportPreviewHeader}`} horizontal horizontalAlign="space-between">
        <Stack.Item className="report-header-name-editor" >
          <NameEditor
            value={reportParam.reportName}
            isLegacyReport={reportParam.isLegacyReport}
            isDirty={isDirty}
            reportAction={reportAction}
            i18n={i18n}
            onNameChange={onNameChange}
            onSave={onClickSave}
            onSaveAs={onClickSaveAs}
            scope={scope}
            shouldHideThisFunctionality={isGenreReport}
          />
        </Stack.Item >
        <Stack.Item className="report-header-timezone-selector" align="end">
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 15 }}>
            {!isAccountScope && <TimeZoneSelector
              onChange={onTimeZoneChange}
              timeZoneValue={reportParam.timeZone}
              i18n={i18n}
              options={timeZones}
              id="timeZone"
              ariaLabelledByID="timezoneLabel"
            />
            }
            <DatePicker
              key={JSON.stringify(reportParam)}
              reportSchema={reportSchema}
              dateRange={reportParam.dateRange}
              onChange={onDatePickerChange}
              timeGrain={convertColumnsToOldUIReportAggregation(reportParam.columns)}
              timeZone={reportParam.timeZone}
              setWarning={_.partial(patchWarnings, 'dateRange')}
            />
            {!isGenreReport &&
              <IconButton
                onClick={onColumnChooserOpen}
                iconProps={{ iconName: 'TripleColumnEdit' }}
                title={i18n.getString(_TL_('Open column chooser'))}
                disabled={false}
                className={`${iconClassNames.blue} ${classes.icons} column-chooser-icon`}
              />
            }
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }

  function renderDeprecatedReportBanner() {
    if (!dimensionBannerVisible) {
      return null;
    }
    return (
      <MessageBar className="dimension-banner" onDismiss={() => setDimensionBannerVisible(false)} dismissButtonAriaLabel="Close">
        <b>{i18n.getString(_TL_("Don't see your dimensions reports? "))}</b>
        {i18n.getString(_TL_('We’ve enhanced the visual experience for dimension reports. Your saved dimension report now redirects to the corresponding default report.'))}
      </MessageBar>
    );
  }

  function renderPlaceholder() {
    return (
      <div className={`${classes.noDataplaceHolder} report-details-no-data`}>
        {placeHolderError ? i18n.getString(placeHolderError) : i18n.getString(_TL_("We ran this report, but we didn't find any data to show that matches your criteria."))}
      </div>
    );
  }

  function getDesclaimerBannerText(reportType) {
    switch (reportType) {
      case 'ConversionModelCompareReport':
        return i18n.getString(_TL_('Not all metrics are in real time. Data from the last 96 hours might not be included. Also, data prior to December 6th 2022 is not available for this report'));
      default:
        // if text is empty, the component will use default text, which is good for most report.
        return '';
    }
  }

  function renderGroupHeader(props1) {
    return (
      <div className="group-header">
        <GroupHeader
          {...props1}
          className="group-header-Chevron"
          indentWidth={0}
          styles={{
            headerCount: { display: 'none' },
          }}
        />
        <DetailsRow
          {...props1}
          role="row"
          itemIndex={-1}
          item={{ ...props1.group }}
        />
      </div>);
  }

  function renderBody() {
    // const isLoading = false;
    const isLoadedAndNoDataFound = reportDataItems && (reportDataItems.length === 0 || (reportDataItems.length === 1 && reportDataItems[0].key === 'Other URLs'));
    const hideGridPagination = isInsertionOrderMonthlyView({
      reportType: reportSchema.ReportType,
      permissions,
      viewType: selectedInsertionOrderViewItem,
    });
    const GridPagination = reportParam.GridPagination || {};
    const gridPaginationInfo = {
      pageNumber: GridPagination.RequestedPage || 1,
      pageSize: GridPagination.RowsPerPage || 20,
      total: totalCount,
    };

    const desclaimerBannerText = getDesclaimerBannerText(reportSchema.ReportType);

    return (
      <div className="body">
        <div className={classNames('padding-container', { [classes.insertionOrderReport]: isInsertionOrderReportEnabled })}>
          <ReportGrid
            items={reportDataItems}
            columns={getDetailListColumns(reportParam, reportSchema, i18n, permissions, isAccountScope)}
            accountSelectorInfo={reportParam.scopeSelector}
            filters={reportParam.filters}
            conditionalFormats={reportParam.conditionalFormats}
            reportSchema={reportSchema}
            pager={gridPaginationInfo}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onAccountSelectorChange={onAccountSelectorChange}
            onDownload={onClickDownload}
            onSort={onSort}
            onConditionalFormatsChange={onConditionalFormatsChange}
            onFilterChange={onFilterChange}
            sortedColumn={reportParam.sortedColumn}
            searchHistorySelectorOptions={searchHistorySelectorOptions}
            onSearchHistorySelectorOptionsChange={setSearchHistorySelectorOptions}
            perfMarker={perfMarker}
            actionPerfMarker={actionPerfMarker}
            shouldHideThisFunctionality={isGenreReport}
            selectedViewItem={selectedInsertionOrderViewItem}
            onViewItemChange={onInsertionOrderViewItemChange}
            onInsertionOrderSelectorChange={onInsertionOrderSelectorChange}
            insertionSelectorInfo={insertionOrderSelected}
            hidePagination={hideGridPagination}
            selectionMode={0}
            groups={categoryGroup}
            groupProps={getGroupProps(renderGroupHeader, onToggleCollapse)}
            isSearchInsightReportGridModal={isSearchInsightReportGridModal}
          />
          {(isLoadedAndNoDataFound || placeHolderError) && renderPlaceholder()}
          <Warning messages={warnings} />
          <DisclaimerBanner timezone={timeZones[reportParam.timeZone]} text={desclaimerBannerText}>
            {reportSchema.ReportType === 'SearchQueryPerformanceReport' &&
              <>
                <br />
                <LearnMoreLink noLink text={i18n.getString(_TL_('A search term report includes only search terms that have had a significant number of clicks in the specified time period. This means the number of impressions listed in a search term report might just be a fraction of the total impressions seen in other performance reports (e.g., Keyword Performance report, Campaign Performance report, etc.).'))} />
              </>
            }
          </DisclaimerBanner>
        </div>
        <ReportsV4Footer />
      </div>
    );
  }

  function renderReportGridModal() {
    const GridPagination = reportModalParam ? reportModalParam.GridPagination : {};
    const sortedColumn = reportModalParam ? reportModalParam.sortedColumn : {};

    const gridPaginationInfo = {
      pageNumber: GridPagination.RequestedPage || 1,
      pageSize: GridPagination.RowsPerPage || 20,
      total: modalTotalCount,
    };

    return (
      <ReportGridModal
        isReportModalOpen={isReportModalOpen}
        hideModal={toggleIsReportModalOpen}
        i18n={i18n}
        items={reportModalDataItems}
        columns={getDetailListColumns(reportParam, reportSchema, i18n, permissions, isAccountScope)}
        onSort={onModalSort}
        sortedColumn={sortedColumn}
        freezingColumnCount={1}
        pager={gridPaginationInfo}
        onPageChange={onModalPageChange}
        onPageSizeChange={onModalPageSizeChange}
        isLoadedAndNoDataFound={modalTotalCount === 0}
      />
    );
  }

  function renderSideReportGridModal() {
    const GridPagination = reportModalParam ? reportModalParam.GridPagination : {};
    const sortedColumn = reportModalParam ? reportModalParam.sortedColumn : {};
    const searchCategoryTitle = reportModalParam ? reportModalParam.SearchCategory : '';

    const gridPaginationInfo = {
      pageNumber: GridPagination.RequestedPage || 1,
      pageSize: GridPagination.RowsPerPage || 20,
      total: modalTotalCount,
    };

    return (
      <SidePanelReportGrid
        isSearchInsightReportOpen={isSearchInsightReportOpen}
        hideSidePanel={toggleIsSearchReportModalOpen}
        i18n={i18n}
        items={reportModalDataItems}
        columns={getDetailListColumns(reportModalParam, reportSchema, i18n, permissions, isAccountScope)}
        onSort={onModalSort}
        sortedColumn={sortedColumn}
        freezingColumnCount={1}
        pager={gridPaginationInfo}
        onPageChange={onModalPageChange}
        onPageSizeChange={onModalPageSizeChange}
        isLoadedAndNoDataFound={modalTotalCount === 0}
        searchCategoryTitle={searchCategoryTitle}
      />
    );
  }

  return (
    <React.Fragment>
      <ReportsV4SummaryBar
        reportName={reportParam.reportName}
        onSchedulerSaveClick={onSchedulerSaveClick}
        schedulerOptions={{
          ...defaultSchedule, ...reportParam.schedule, FileFormat: reportParam.Format, Template: reportParam.Type,
        }}
        typeOfReport={reportParam.Type}
        timeTravelOptions={
          {
            isRedoEnabled: timeTravelHistory.isForwardValid,
            isUndoEnabled: timeTravelHistory.isBackValid,
            isResetEnabled: isDirty,
            onRedoClick,
            onUndoClick,
            onResetClick,
          }
        }
        forceRefreshData={forceRefreshData}
        shouldHideThisFunctionality={isGenreReport}
      />
      <div className="content-wrapper">
        <div className={`main-view ${isColumnChooserOpened ? 'right-panel-expand' : ''}`}>
          {renderHeader()}
          {renderDeprecatedReportBanner()}
          {!dimensionBannerVisible && <Separator className="seperator-view" />}
          {renderBody()}
          <NavigateDialog
            key={reportId + reportSchema.ReportType}
            i18n={i18n}
            disableNavigateDialog={!isDirty}
            disableNavigateDialogOnce={disableNavigateDialogOnce}
            setDisableNavigateDialogOnce={setDisableNavigateDialogOnce}
          />
        </div>
        {
          hasReportGridModal && renderReportGridModal()
        }
        {isSearchInsightReportGridModal && renderSideReportGridModal()}
        {
          isColumnChooserOpened &&
          <div className="right-panel-view">
            {renderColumnChooer()}
          </div>
        }
      </div>
    </React.Fragment>
  );
}

